import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  OpenInNew,
  AccessTime,
  PersonOutline,
  FormatQuote,
  InsertInvitation,
  EventBusy,
  DoDisturbAlt,
  Description,
  Groups,
  Summarize,
} from '@mui/icons-material';

import { type CourtDocket, type Court } from '../../../types';
import { formatCourtDateWithTime } from '../../../utils';

import {
  HeaderCard,
  HeaderTopSection,
  TitleRow,
  TitleContainer,
  CaseTitle,
  ExternalLinkIcon,
  LastUpdatedContainer,
  LastUpdatedIcon,
  LastUpdatedText,
  CourtNameLink,
  DetailsGrid,
  DetailPill,
  DetailPillContent,
  DetailIconContainer,
  DetailLabel,
  DetailValue,
} from './components';

interface CourtCaseDetailsHeaderProps {
  selectedDocket: CourtDocket;
  courtInfo: Court | null;
}

export const CourtCaseDetailsHeader = ({
  selectedDocket,
  courtInfo,
}: CourtCaseDetailsHeaderProps) => {
  const { links } = useAppConstantsContext();

  // Helper function to get court display text
  const getCourtDisplayText = () => {
    return courtInfo?.full_name || selectedDocket.court_id || 'Not Available';
  };

  const handleCaseNameClick = () => {
    const legalSearchUrl = `/dashboard/court-cases?query=${encodeURIComponent(
      `caseName:${selectedDocket.case_name}`
    )}`;
    window.open(
      `${links.baseUrl}${legalSearchUrl}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleCourtClick = () => {
    if (selectedDocket.court_id) {
      const courtName = courtInfo?.full_name || '';
      const legalSearchUrl = `/dashboard/court-cases?query=${encodeURIComponent(
        `court_id:${selectedDocket.court_id}`
      )}${courtName ? `&courtName=${encodeURIComponent(courtName)}` : ''}`;
      window.open(
        `${links.baseUrl}${legalSearchUrl}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const citationText = `${selectedDocket.case_name}, ${
    selectedDocket.docket_number || ''
  }, (${selectedDocket.court_id?.toUpperCase() || ''})`;

  return (
    <HeaderCard>
      <HeaderTopSection>
        <TitleRow>
          <TitleContainer>
            <CaseTitle>{selectedDocket.case_name}</CaseTitle>
            <ExternalLinkIcon onClick={handleCaseNameClick}>
              <OpenInNew />
            </ExternalLinkIcon>
          </TitleContainer>
          <LastUpdatedContainer>
            <LastUpdatedIcon>
              <AccessTime />
            </LastUpdatedIcon>
            <LastUpdatedText>
              Last Updated:{' '}
              {formatCourtDateWithTime(selectedDocket.date_modified)}
            </LastUpdatedText>
          </LastUpdatedContainer>
        </TitleRow>
        <CourtNameLink
          onClick={selectedDocket.court_id ? handleCourtClick : undefined}
          sx={{
            cursor: selectedDocket.court_id ? 'pointer' : 'default',
            textDecoration: selectedDocket.court_id ? 'underline' : 'none',
          }}
        >
          {getCourtDisplayText()}
        </CourtNameLink>
      </HeaderTopSection>

      <DetailsGrid>
        {/* Left Column */}
        {/* Assigned To */}
        {selectedDocket.assigned_to_str && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <PersonOutline />
              </DetailIconContainer>
              <DetailLabel>Assigned To:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.assigned_to_str}</DetailValue>
          </DetailPill>
        )}

        {/* Referred To */}
        {selectedDocket.referred_to_str && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <PersonOutline />
              </DetailIconContainer>
              <DetailLabel>Referred To:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.referred_to_str}</DetailValue>
          </DetailPill>
        )}

        {/* Citation */}
        <DetailPill>
          <DetailPillContent>
            <DetailIconContainer>
              <FormatQuote />
            </DetailIconContainer>
            <DetailLabel>Citation</DetailLabel>
          </DetailPillContent>
          <DetailValue>{citationText}</DetailValue>
        </DetailPill>

        {/* Date Filed */}
        {selectedDocket.date_filed && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <InsertInvitation />
              </DetailIconContainer>
              <DetailLabel>Date Filed</DetailLabel>
            </DetailPillContent>
            <DetailValue>
              {formatCourtDateWithTime(selectedDocket.date_filed)}
            </DetailValue>
          </DetailPill>
        )}

        {/* Date of Last Known Filing */}
        {selectedDocket.date_last_filing && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <AccessTime />
              </DetailIconContainer>
              <DetailLabel>Last Known Filing Date:</DetailLabel>
            </DetailPillContent>
            <DetailValue>
              {formatCourtDateWithTime(selectedDocket.date_last_filing)}
            </DetailValue>
          </DetailPill>
        )}

        {/* Right Column */}
        {/* Date Terminated */}
        {selectedDocket.date_terminated && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <EventBusy />
              </DetailIconContainer>
              <DetailLabel>Date Terminated:</DetailLabel>
            </DetailPillContent>
            <DetailValue>
              {formatCourtDateWithTime(selectedDocket.date_terminated)}
            </DetailValue>
          </DetailPill>
        )}

        {/* Cause */}
        {selectedDocket.cause && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <DoDisturbAlt />
              </DetailIconContainer>
              <DetailLabel>Cause</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.cause}</DetailValue>
          </DetailPill>
        )}

        {/* Nature of Suit */}
        {selectedDocket.nature_of_suit && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <Description />
              </DetailIconContainer>
              <DetailLabel>Nature of Suit</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.nature_of_suit}</DetailValue>
          </DetailPill>
        )}

        {/* Jury Demand */}
        {selectedDocket.jury_demand && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <Groups />
              </DetailIconContainer>
              <DetailLabel>Jury Demand</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.jury_demand}</DetailValue>
          </DetailPill>
        )}

        {/* Jurisdiction Type */}
        {selectedDocket.jurisdiction_type && (
          <DetailPill>
            <DetailPillContent>
              <DetailIconContainer>
                <Summarize />
              </DetailIconContainer>
              <DetailLabel>Jurisdiction Type:</DetailLabel>
            </DetailPillContent>
            <DetailValue>{selectedDocket.jurisdiction_type}</DetailValue>
          </DetailPill>
        )}
      </DetailsGrid>
    </HeaderCard>
  );
};
